import React, { useState, useRef, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Grid,
} from "@mui/material"
import {
  Close as CloseIcon,
  Print as PrintIcon,
  FileDownload as DownloadIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material"
import { useReactToPrint } from "react-to-print"
import * as XLSX from "xlsx"
import { useApiQuery } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"

function SettlementReportGenerator({ open, onClose }) {
  const [filterType, setFilterType] = useState("month")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const reportRef = useRef()

  // Compute URL dynamically
  const reportUrl = useMemo(() => {
    const params = new URLSearchParams()
    
    if (filterType === "custom" && startDate && endDate) {
      params.append("startDate", startDate)
      params.append("endDate", endDate)
    } else {
      params.append("filter", filterType)
    }
    
    return `/settlements?${params.toString()}`
  }, [filterType, startDate, endDate])

  const queryClient = useQueryClient()

  // useApiQuery integration
  const {
    data: settlementsData,
    error,
    isFetching,
    refetch,
  } = useApiQuery(["settlement-report", reportUrl], reportUrl, {
    enabled: false, // don't auto-fetch; fetch manually
  })

  // Process settlements data into report format
  const reportData = useMemo(() => {
    if (!settlementsData || !Array.isArray(settlementsData)) return null

    const settlements = settlementsData

    // Calculate summary statistics
    const totalSettlements = settlements.length
    const totalAmountSettled = settlements.reduce((sum, s) => sum + (s.totalUsed || 0), 0)
    const totalBalanceCarried = settlements.reduce((sum, s) => sum + (s.balanceCarried || 0), 0)
    const totalAmountFromBalance = settlements.reduce((sum, s) => sum + (s.amountUsedFromBalance || 0), 0)

    // Get date range
    const dates = settlements.map(s => new Date(s.createdAt))
    const startDateObj = dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : new Date()
    const endDateObj = dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : new Date()

    // Group by settler
    const bySettler = settlements.reduce((acc, s) => {
      const settlerName = s.settledBy?.name || "Unknown"
      if (!acc[settlerName]) {
        acc[settlerName] = {
          count: 0,
          totalUsed: 0,
          balanceCarried: 0,
        }
      }
      acc[settlerName].count += 1
      acc[settlerName].totalUsed += s.totalUsed || 0
      acc[settlerName].balanceCarried += s.balanceCarried || 0
      return acc
    }, {})

    return {
      summary: {
        totalSettlements,
        totalAmountSettled,
        totalBalanceCarried,
        totalAmountFromBalance,
        bySettler,
        dateRange: {
          start: startDateObj,
          end: endDateObj,
        },
      },
      settlements: settlements.map(s => ({
        settlementId: s._id,
        jobId: s.jobId,
        clientName: s.clientId?.name || "N/A",
        clientPhone: s.clientId?.phoneNumber || "N/A",
        totalUsed: s.totalUsed || 0,
        balanceCarried: s.balanceCarried || 0,
        amountUsedFromBalance: s.amountUsedFromBalance || 0,
        receiptsCount: s.receipts?.length || 0,
        settledBy: s.settledBy?.name || "N/A",
        remarks: s.remarks || "",
        date: s.createdAt,
      }))
    }
  }, [settlementsData])

  const handleGenerateReport = () => {
    refetch()
  }

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Settlement_Report_${new Date().toISOString().split("T")[0]}`,
  })

  const handleDownloadExcel = () => {
    if (!reportData) return

    const workbook = XLSX.utils.book_new()

    // Summary Sheet
    const summaryData = [
      ["Fee Settlement Report"],
      [""],
      ["Report Period", `${reportData.summary.dateRange.start.toLocaleDateString()} - ${reportData.summary.dateRange.end.toLocaleDateString()}`],
      ["Total Settlements", reportData.summary.totalSettlements],
      ["Total Amount Settled", `₹${reportData.summary.totalAmountSettled.toLocaleString()}`],
      ["Total Balance Carried", `₹${reportData.summary.totalBalanceCarried.toLocaleString()}`],
      ["Total Used from Client Balance", `₹${reportData.summary.totalAmountFromBalance.toLocaleString()}`],
      [""],
      ["Settlement by Settler"],
      ["Settler Name", "Count", "Total Settled", "Balance Carried"],
    ]

    Object.entries(reportData.summary.bySettler).forEach(([settler, data]) => {
      summaryData.push([
        settler,
        data.count,
        data.totalUsed,
        data.balanceCarried,
      ])
    })

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary")

    // Settlements Sheet
    const settlementsData = [
      [
        "Settlement ID",
        "Date",
        "Job ID",
        "Client Name",
        "Client Phone",
        "Amount Settled",
        "Balance Carried",
        "Used from Balance",
        "Receipts Count",
        "Settled By",
        "Remarks",
      ],
    ]

    reportData.settlements.forEach((settlement) => {
      settlementsData.push([
        settlement.settlementId,
        new Date(settlement.date).toLocaleString(),
        settlement.jobId,
        settlement.clientName,
        settlement.clientPhone,
        settlement.totalUsed,
        settlement.balanceCarried,
        settlement.amountUsedFromBalance,
        settlement.receiptsCount,
        settlement.settledBy,
        settlement.remarks,
      ])
    })

    const settlementsSheet = XLSX.utils.aoa_to_sheet(settlementsData)
    XLSX.utils.book_append_sheet(workbook, settlementsSheet, "Settlements")

    // Download
    XLSX.writeFile(workbook, `Settlement_Report_${new Date().toISOString().split("T")[0]}.xlsx`)
  }

  const handleDownloadPDF = () => {
    handlePrint()
  }

  const getFilterLabel = () => {
    switch (filterType) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      case "custom":
        return "Custom Range"
      default:
        return "This Month"
    }
  }

  const handleClose = () => {
    queryClient.removeQueries(["settlement-report", reportUrl])
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            Generate Settlement Report
          </Typography>
          <Button onClick={handleClose} color="inherit">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Filter Selection */}
        {!reportData && (
          <Box sx={{ mb: 3 }}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Report Period</InputLabel>
                <Select
                  value={filterType}
                  label="Report Period"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="30days">Last 30 Days</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="custom">Custom Date Range</MenuItem>
                </Select>
              </FormControl>

              {filterType === "custom" && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
              )}

              {error && (
                <Alert severity="error">
                  {error.message}
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={handleGenerateReport}
                disabled={isFetching || (filterType === "custom" && (!startDate || !endDate))}
                startIcon={isFetching ? <CircularProgress size={20} /> : <CalendarIcon />}
              >
                {isFetching ? "Generating..." : "Generate Report"}
              </Button>
            </Stack>
          </Box>
        )}

        {/* Report Display */}
        {reportData && (
          <Box>
            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleDownloadPDF}>
                Print / Save as PDF
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadExcel}>
                Download Excel
              </Button>
            </Stack>

            {/* Printable Report */}
            <Box ref={reportRef} sx={{ p: 3, bgcolor: "white" }}>
              {/* Report Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Fee Settlement Report
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {reportData.summary.dateRange.start.toLocaleDateString()} -{" "}
                  {reportData.summary.dateRange.end.toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Generated on: {new Date().toLocaleString()}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Summary Statistics */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "primary.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Total Settlements
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {reportData.summary.totalSettlements}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "success.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Total Amount Settled
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ₹{reportData.summary.totalAmountSettled.toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "info.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Balance Carried Forward
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ₹{reportData.summary.totalBalanceCarried.toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: "warning.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Used from Balance
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ₹{reportData.summary.totalAmountFromBalance.toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Settlement by Settler Breakdown */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Settlement by Settler
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Settler Name</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Count</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total Settled</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Balance Carried</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {Object.entries(reportData.summary.bySettler).map(([settler, data]) => (
                        <TableRow key={settler}>
                          <TableCell>
                            <Chip label={settler} size="small" />
                          </TableCell>
                          <TableCell align="right">{data.count}</TableCell>
                          <TableCell align="right">₹{data.totalUsed.toLocaleString()}</TableCell>
                          <TableCell align="right">₹{data.balanceCarried.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Detailed Settlements */}
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Detailed Settlements
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Job ID</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Client</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Amount Settled</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Balance Carried</strong>
                        </TableCell>
                        {/* <TableCell align="right">
                          <strong>From Balance</strong>
                        </TableCell> */}
                        {/* <TableCell align="center">
                          <strong>Receipts</strong>
                        </TableCell> */}
                        <TableCell>
                          <strong>Settled By</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.settlements.map((settlement) => (
                        <TableRow key={settlement.settlementId}>
                          <TableCell>
                            {new Date(settlement.date).toLocaleDateString()}
                            <Typography variant="caption" display="block" color="textSecondary">
                              {new Date(settlement.date).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>
                            {settlement.jobId}
                          </TableCell>
                          <TableCell>
                            {settlement.clientName}
                            <Typography variant="caption" display="block" color="textSecondary">
                              {settlement.clientPhone}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold", color: "success.main" }}>
                            ₹{settlement.totalUsed.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            ₹{settlement.balanceCarried.toLocaleString()}
                          </TableCell>
                          {/* <TableCell align="right">
                            {settlement.amountUsedFromBalance > 0 ? (
                              <Chip
                                label={`₹${settlement.amountUsedFromBalance.toLocaleString()}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            ) : (
                              "—"
                            )}
                          </TableCell> */}
                          {/* <TableCell align="center">
                            <Chip label={settlement.receiptsCount} size="small" />
                          </TableCell> */}
                          <TableCell>{settlement.settledBy}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Footer */}
              <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #ddd", textAlign: "center" }}>
                <Typography variant="caption" color="textSecondary">
                  This is a computer-generated report. For any queries, please contact the administration.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default SettlementReportGenerator